import React, { useEffect, useState, useMemo } from "react";

import { CheckCircle, ThumbsDown, Star, MessageCircle, X } from "lucide-react";
import { approveProductReview, getProductReviewsByStatus, rejectProductReview } from "../../services/woocommerce";
import LoadingScreen from "../../components/LoadingScreen";


interface Review {
    id: number;
    reviewer: string;
    reviewer_email: string;
    rating: number;
    product_name: string;
    date_created: string;
    review: string;
    product_id: number;
    product_permalink: string;
    verified: boolean;
}

const TABS = [
    { label: "Pending", status: "hold" },
    { label: "Approved", status: "approved" },
    { label: "Spam", status: "spam" },
    { label: "Trash", status: "trash" },
];

// ====== Custom Hook for Reviews ======
const useReviews = (status: string) => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchReviews = async () => {
        setLoading(true);
        try {
            const res = await getProductReviewsByStatus(status);
            setReviews(res.data);
        } catch (err) {
            console.error("Error fetching reviews", err);
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (id: number, action: "approve" | "reject") => {
        try {
            if (action === "approve") await approveProductReview(id);
            if (action === "reject") await rejectProductReview(id);
            fetchReviews();
        } catch (err) {
            console.error(`Failed to ${action} review`, err);
        }
    };

    const handleBulkAction = async (ids: number[], action: "approve" | "reject") => {
        for (const id of ids) {
            await handleAction(id, action);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, [status]);

    return { reviews, loading, fetchReviews, handleAction, handleBulkAction };
};

// ====== Tabs Component ======
const TabsDropdown: React.FC<{
    tabs: typeof TABS;
    activeTab: string;
    onChange: (status: string) => void;
}> = ({ tabs, activeTab, onChange }) => (
    <div className="w-full md:w-auto flex items-center gap-4" >
        <select
            value={activeTab}
            onChange={(e) => onChange(e.target.value)}
            className="px-3 py-1.5 border rounded-md text-sm w-full md:w-auto"
        >
            {tabs.map((tab) => (
                <option key={tab.status} value={tab.status}>
                    {tab.label}
                </option>
            ))}
        </select>
    </div >
);



// ====== Search Component ======
const SearchBar: React.FC<{
    value: string;
    onChange: (val: string) => void;
    onClear: () => void;
}> = ({ value, onChange, onClear }) => (
    <div className="relative w-full md:w-1/2">
        <input
            type="text"
            placeholder="Search reviews..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="px-4 py-2 w-full border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
        {value && (
            <button
                onClick={onClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
                <X size={20} />
            </button>
        )}
    </div>
);

// ====== Bulk Actions Component ======
const BulkActions: React.FC<{
    selectedIds: number[];
    onAction: (action: "approve" | "reject") => void;
}> = ({ selectedIds, onAction }) =>
        selectedIds.length > 0 ? (
            <div className="flex gap-3 mb-4">
                <button
                    onClick={() => onAction("approve")}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-full text-sm"
                >
                    Approve Selected
                </button>
                <button
                    onClick={() => onAction("reject")}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-full text-sm"
                >
                    Reject Selected
                </button>
            </div>
        ) : null;

// ====== Review Card Component ======
const ReviewCard: React.FC<{
    review: Review;
    selected: boolean;
    toggleSelect: (id: number) => void;
    onAction: (id: number, action: "approve" | "reject") => void;
    showActions: boolean;
}> = ({ review, selected, toggleSelect, onAction, showActions }) => (
    <div
        className={`bg-white/70 border backdrop-blur-md p-6 rounded-3xl shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group ${review.verified ? "border-emerald-300" : "border-gray-200"
            }`}
    >
        <div className="flex justify-between items-start mb-3">
            <div className="flex items-start gap-2">
                <input
                    type="checkbox"
                    className="mt-1"
                    checked={selected}
                    onChange={() => toggleSelect(review.id)}
                />
                <div>
                    <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                        {review.reviewer}
                        {review.verified && (
                            <span className="text-xs bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full font-medium">
                                Verified
                            </span>
                        )}
                    </h3>
                    <p className="text-sm text-gray-500">{review.reviewer_email}</p>
                </div>
            </div>
        </div>

        <div className="flex gap-[2px] mb-4">
            {[...Array(5)].map((_, i) => (
                <Star
                    key={i}
                    size={18}
                    fill={i < review.rating ? "#facc15" : "#e5e7eb"}
                    className="stroke-yellow-500"
                />
            ))}
        </div>

        <ul className="text-sm text-gray-600 space-y-1 mb-3">
            <li>
                <strong>Review ID:</strong> {review.id}
            </li>
            <li>
                <strong>Product ID:</strong> {review.product_id}
            </li>
            <li>
                <strong>Date:</strong> {new Date(review.date_created).toLocaleString()}
            </li>
        </ul>

        <div className="text-gray-700 text-sm mb-4 leading-relaxed max-h-36 overflow-y-auto break-words pr-2">
            <div
                className="whitespace-pre-wrap break-words"
                dangerouslySetInnerHTML={{ __html: review.review }}
            />
        </div>

        <a
            href={review.product_permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline text-sm font-medium mb-2 block"
        >
            View Product: {review.product_name}
        </a>

        {showActions && (
            <div className="flex gap-3 mt-4">
                <button
                    onClick={() => onAction(review.id, "approve")}
                    className="flex items-center gap-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-4 py-1.5 rounded-full text-sm shadow-md transition"
                >
                    <CheckCircle size={16} /> Approve
                </button>
                <button
                    onClick={() => onAction(review.id, "reject")}
                    className="flex items-center gap-1 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white px-4 py-1.5 rounded-full text-sm shadow-md transition"
                >
                    <ThumbsDown size={16} /> Reject
                </button>
            </div>
        )}
    </div>
);

// ====== Main Panel ======
const ReviewModerationPanel: React.FC = () => {
    const [activeTab, setActiveTab] = useState("hold");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const { reviews, loading, handleAction, handleBulkAction } = useReviews(activeTab);

    const toggleSelection = (id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const filteredReviews = useMemo(
        () =>
            reviews.filter(
                (r) =>
                    r.reviewer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    r.reviewer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    r.product_name.toLowerCase().includes(searchTerm.toLowerCase())
            ),
        [reviews, searchTerm]
    );

    const sortedReviews = useMemo(
        () =>
            [...filteredReviews].sort((a, b) =>
                sortOrder === "desc"
                    ? new Date(b.date_created).getTime() - new Date(a.date_created).getTime()
                    : new Date(a.date_created).getTime() - new Date(b.date_created).getTime()
            ),
        [filteredReviews, sortOrder]
    );

    if (loading) return <LoadingScreen message="Fetching Reviews..." layout={false} />;

    return (
        <div>


            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                {/* Left side: Search Bar */}
                <div className="flex-1 min-w-0">
                    <SearchBar
                        value={searchTerm}
                        onChange={setSearchTerm}
                        onClear={() => setSearchTerm("")}
                    />
                </div>

                {/* Middle: Tabs Dropdown */}
                <div className="w-full md:w-auto">
                    <TabsDropdown tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
                </div>

                {/* Right side: Sort Order */}
                <div className="w-full md:w-auto flex items-center gap-4">
                    <select
                        className="px-3 py-1.5 border rounded-md text-sm w-full md:w-auto"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
                    >
                        <option value="desc">Newest First</option>
                        <option value="asc">Oldest First</option>
                    </select>
                </div>
            </div>


            <BulkActions selectedIds={selectedIds} onAction={(action) => handleBulkAction(selectedIds, action)} />

            {sortedReviews.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400 animate-fadeIn">
                    <MessageCircle className="w-14 h-14 mb-3 text-indigo-400" />
                    <p className="italic text-lg">
                        No reviews under <strong>{activeTab}</strong>.
                    </p>
                </div>
            ) : (
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {sortedReviews.map((review) => (
                        <ReviewCard
                            key={review.id}
                            review={review}
                            selected={selectedIds.includes(review.id)}
                            toggleSelect={toggleSelection}
                            onAction={handleAction}
                            showActions={activeTab === "hold"}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReviewModerationPanel;
